'use client'
import { useLayoutContext } from '../../../providers/layout-provider'
import React, { ReactElement, useEffect } from 'react'

interface LayoutDelegatorProps {
    children: ReactElement | ReactElement[]
}

/**
 * @deprecated This component was only written to make the Layout component compatible with legacy code
 * @description
 * The LayoutDelegator component is used in the same way as the Layout component,
 * but always as a (grand)child of one of the LayoutPanes.
 * A LayoutProvider should be used as a parent of the Layout component.
 *
 * A LayoutPane that is defined as a direct child of the LayoutDelegator
 *   will replace the LayoutPane of the same paneType that is a direct child of the Layout.
 * If no such LayoutPane was defined in the Layout. The LayoutPane will just be added to the Layout.
 *
 * The LayoutPaneType in which the LayoutDelegator is defined, should not be used an in the LayoutDelegator.
 * Unexpected behavior might occur if this is done.
 * @todo investigate if this would actually cause problems, and if it does, prevent it from happening.
 *
 * @example
 * <LayoutProvider>
 *   <Layout>
 *
 *     <LayoutSidebarPane>
 *
 *       <div>sidebar ui</div>
 *
 *       {true && <LayoutDelegator>
 *         <LayoutContentPane>
 *           This LayoutContentPane replaces the LayoutContentPane that is a direct child of the Layout
 *         </LayoutContentPane>
 *       </LayoutDelegator>}
 *
 *     </LayoutSidebarPane>
 *
 *     <LayoutContentPane>Direct Child Of Layout</LayoutContentPane>
 *
 *   </Layout>
 * </LayoutProvider>
 */
export const LayoutDelegator = ({ children }: LayoutDelegatorProps): JSX.Element => {
    const childrenAsArray = React.Children.toArray(children) as ReactElement[]
    const { layoutState, setLayoutState, cleanUpContext } = useLayoutContext()

    /**
     * Gets whether children are not of type Layout Pane.
     * Those components should not be direct children of this component.
     */
    const getChildrenNotOfTypeLayoutPane = () =>
        childrenAsArray.some((child) => {
            return !/sidebar|bottom-pane|top-pane|content/.test(child.props.paneType)
        })

    useEffect(() => {
        const childrenNotOfTypeLayoutPane = getChildrenNotOfTypeLayoutPane()

        /** Only set the layout if the children are of correct type */
        if (!childrenNotOfTypeLayoutPane) {
            setLayoutState({ ...layoutState, layoutPanes: childrenAsArray })
        }
    }, [children])

    useEffect(() => {
        return () => {
            cleanUpContext()
        }
    }, [])

    return <></>
}
